import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { fmtBDT, fmtBDTShort, fmtDateShort, fmtNum } from "../lib/format";
import {
  ArrowDownToLine, ArrowUpFromLine, UsersRound,
  Coins,
  UserPlus, CalendarRange, CalendarDays, FileDown,
  AlertTriangle, ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [stuck, setStuck] = useState({ count: 0, total_stake: 0, oldest_min: 0, loading: true });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/dashboard/stats");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Stuck bets widget — independent fetch, auto-refresh every 60s
  useEffect(() => {
    const fetchStuck = async () => {
      try {
        const { data } = await api.get("/admin/pending-bets-summary", {
          params: { min_age_minutes: 30 },
        });
        setStuck({
          count: data?.count || 0,
          total_stake: data?.total_stake || 0,
          oldest_min: data?.oldest_minutes || 0,
          loading: false,
        });
      } catch {
        setStuck((p) => ({ ...p, loading: false }));
      }
    };
    fetchStuck();
    const id = setInterval(fetchStuck, 60_000);
    return () => clearInterval(id);
  }, []);

  const pending = data?.pending || {};
  const periods = data?.periods || {};
  const pToday = periods.today || {};
  const pYesterday = periods.yesterday || {};
  const p3Days = periods.last_3_days || {};
  const pWeek = periods.last_7_days || {};
  const p15Days = periods.last_15_days || {};
  const pMonth = periods.last_30_days || {};
  const chart = (data?.chart || []).map((d) => ({ ...d, label: fmtDateShort(d.date) }));

  const pendingSum = (pending.deposits || 0) + (pending.withdrawals || 0) + (pending.bets || 0);
  const pieData = [
    { name: "Pending Deposits", value: pending.deposits || 0 },
    { name: "Pending Withdrawals", value: pending.withdrawals || 0 },
    { name: "Pending Bets", value: pending.bets || 0 },
  ];

  const downloadSnapshotPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/dashboard/snapshot.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `dashboard-snapshot-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Live operational overview · all figures in BDT · Bangladesh Time (Asia/Dhaka)"
        testId="dashboard-header"
        actions={
          <button
            onClick={downloadSnapshotPdf}
            disabled={downloading || loading}
            className="h-9 px-3 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-[13px] font-semibold disabled:opacity-50"
            data-testid="dashboard-download-pdf"
            title="One-click PDF — Today / Last 7 Days / Last 30 Days · Registration, Deposit, Withdraw, Agents Created"
          >
            <FileDown size={13} /> {downloading ? "Generating…" : "Download PDF"}
          </button>
        }
      />

      {/* Period Stat Rows — Today / Yesterday / Last 3 Days / Last 7 Days / Last 30 Days (Bangladesh time) */}
      <PeriodRow
        label="Today"
        accent="emerald"
        icon={CalendarDays}
        block={pToday}
        loading={loading}
        testId="period-today"
      />
      <PeriodRow
        label="Yesterday"
        accent="violet"
        icon={CalendarDays}
        block={pYesterday}
        loading={loading}
        testId="period-yesterday"
      />
      <PeriodRow
        label="Last 3 Days"
        accent="rose"
        icon={CalendarRange}
        block={p3Days}
        loading={loading}
        testId="period-3days"
      />
      <PeriodRow
        label="Last 7 Days"
        accent="amber"
        icon={CalendarRange}
        block={pWeek}
        loading={loading}
        testId="period-week"
      />
      <PeriodRow
        label="Last 15 Days"
        accent="teal"
        icon={CalendarRange}
        block={p15Days}
        loading={loading}
        testId="period-15days"
      />
      <PeriodRow
        label="Last 30 Days"
        accent="blue"
        icon={CalendarRange}
        block={pMonth}
        loading={loading}
        testId="period-month"
      />

      {/* Bet Statement Chart */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-sm p-5 xl:col-span-2" data-testid="chart-bet-statement">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Bet Statement</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Last 31 days · Debit vs Credit</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" /> Credit</div>
              <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500 inline-block" /> Debit</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <AreaChart data={chart} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="credit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="debit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" fontSize={10} tick={{ fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis fontSize={10} tick={{ fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtBDTShort(v)} />
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #E5E7EB", borderRadius: 2 }} formatter={(v, k) => [fmtBDT(v), k === "credit" ? "Credit" : "Debit"]} />
                <Area type="monotone" dataKey="credit" stroke="#10B981" strokeWidth={2} fill="url(#credit)" />
                <Area type="monotone" dataKey="debit" stroke="#EF4444" strokeWidth={2} fill="url(#debit)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pending Queue Pie */}
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="pending-pie">
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Pending Queue</div>
            <div className="font-display text-xl font-bold text-gray-900 mt-0.5">{fmtNum(pendingSum)} items awaiting action</div>
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #E5E7EB", borderRadius: 2 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            {pieData.map((p, i) => (
              <div key={p.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} /> {p.name}</div>
                <span className="font-semibold tabular">{fmtNum(p.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stuck Bets banner — only renders when there's an actionable count */}
      <StuckBetsBanner stuck={stuck} />

      {/* Recent Transactions — Site Direct + Agent-Mediated (side-by-side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <TxnFeed
          title="Site Recent Transactions"
          subtitle="Direct deposits & withdrawals on the site"
          accent="emerald"
          rows={data?.recent_site_transactions || []}
          emptyMessage="No site transactions yet."
          testId="site-recent-tx"
        />
        <TxnFeed
          title="Agent Recent Transactions"
          subtitle="Deposits & withdrawals via partner agents"
          accent="amber"
          rows={data?.recent_agent_transactions || []}
          emptyMessage="No agent transactions yet."
          testId="agent-recent-tx"
          showAgent
        />
      </div>
    </div>
  );
}

function TxnFeed({ title, subtitle, accent = "emerald", rows, emptyMessage, testId, showAgent = false }) {
  const accentMap = {
    emerald: "text-emerald-500",
    amber: "text-amber-500",
  };
  return (
    <div className="bg-white border border-gray-200 rounded-sm" data-testid={testId}>
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Live Feed</div>
          <div className="font-display text-lg font-bold">{title}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>
        </div>
        <Coins size={18} className={accentMap[accent] || "text-gray-400"} />
      </div>
      <div className="divide-y divide-gray-100">
        {(rows || []).map((t, i) => {
          // Prefer username over full name for the headline (admin clarity).
          const playerUsername =
            t.player_user_id || t.username || t.player_name || "—";
          const playerNumericId = t.player_id || "";
          const agentUsername = t.agent_username || t.agent_name || "—";
          const isDeposit = t.type === "deposit";
          const typeLabel = isDeposit ? "Deposit" : "Withdraw";
          const typePillCls = isDeposit
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-rose-50 text-rose-700 border-rose-200";
          return (
          <div key={t.id || i} className="flex items-center justify-between px-5 py-3" data-testid={`${testId}-row-${i}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-8 w-8 rounded-sm flex items-center justify-center flex-shrink-0 ${isDeposit ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                {isDeposit ? <ArrowDownToLine size={15} /> : <ArrowUpFromLine size={15} />}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${typePillCls}`}
                    data-testid={`${testId}-row-${i}-type`}
                  >
                    {typeLabel}
                  </span>
                  <div className="text-sm font-semibold text-gray-900 truncate" data-testid={`${testId}-row-${i}-username`}>
                    {playerUsername}
                  </div>
                </div>
                <div className="text-[11px] text-gray-500 truncate mt-0.5">
                  {showAgent ? (
                    <>via <span className="font-semibold text-gray-700" data-testid={`${testId}-row-${i}-agent-username`}>{agentUsername}</span></>
                  ) : (
                    playerNumericId
                      ? <span className="font-mono" data-testid={`${testId}-row-${i}-player-id`}>ID #{playerNumericId}</span>
                      : <span className="font-mono">{t.txn_id || t.id || "—"}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
              <div className={`text-sm font-semibold tabular ${isDeposit ? "text-emerald-700" : "text-red-700"}`}>
                {isDeposit ? "+" : "-"}{fmtBDT(t.amount)}
              </div>
            </div>
          </div>
          );
        })}
        {(rows || []).length === 0 && <div className="px-5 py-10 text-center text-sm text-gray-500">{emptyMessage}</div>}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`h-10 w-10 ${bg} ${color} rounded-sm border border-current/10 flex items-center justify-center flex-shrink-0`}>
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <div className="font-display text-lg font-bold text-gray-900 tabular truncate">{value}</div>
      </div>
    </div>
  );
}

function GgrCard() {
  return null; // deprecated — replaced by PeriodRow above
}

function PeriodRow({ label, accent = "emerald", icon: Icon, block, loading, testId }) {
  const accentMap = {
    emerald: { ring: "ring-emerald-200", iconBg: "bg-emerald-50 text-emerald-700", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    amber:   { ring: "ring-amber-200",   iconBg: "bg-amber-50 text-amber-700",     chip: "bg-amber-50 text-amber-700 border-amber-200" },
    blue:    { ring: "ring-blue-200",    iconBg: "bg-blue-50 text-blue-700",       chip: "bg-blue-50 text-blue-700 border-blue-200" },
    violet:  { ring: "ring-violet-200",  iconBg: "bg-violet-50 text-violet-700",   chip: "bg-violet-50 text-violet-700 border-violet-200" },
    rose:    { ring: "ring-rose-200",    iconBg: "bg-rose-50 text-rose-700",       chip: "bg-rose-50 text-rose-700 border-rose-200" },
    teal:    { ring: "ring-teal-200",    iconBg: "bg-teal-50 text-teal-700",       chip: "bg-teal-50 text-teal-700 border-teal-200" },
  }[accent] || { ring: "ring-gray-200", iconBg: "bg-gray-50 text-gray-700", chip: "bg-gray-50 text-gray-700 border-gray-200" };

  // 6 KPI tiles per period — matches the production layout (Total Registration,
  // Site Deposit, Agent Deposit, Site Withdraw, Agent Withdraw, Agents Created).
  const cards = [
    { label: "Total Registration", icon: UserPlus,         color: "text-blue-600",    bg: "bg-blue-50",    value: loading ? "—" : fmtNum(block?.registrations || 0) },
    { label: "Site Deposit",       icon: ArrowDownToLine,  color: "text-emerald-600", bg: "bg-emerald-50", value: loading ? "—" : fmtBDT(block?.site_deposit ?? 0) },
    { label: "Agent Deposit",      icon: ArrowDownToLine,  color: "text-emerald-700", bg: "bg-emerald-50", value: loading ? "—" : fmtBDT(block?.agent_deposit ?? 0) },
    { label: "Site Withdraw",      icon: ArrowUpFromLine,  color: "text-red-600",     bg: "bg-red-50",     value: loading ? "—" : fmtBDT(block?.site_withdraw ?? 0) },
    { label: "Agent Withdraw",     icon: ArrowUpFromLine,  color: "text-red-700",     bg: "bg-red-50",     value: loading ? "—" : fmtBDT(block?.agent_withdraw ?? 0) },
    { label: "Agents Created",     icon: UsersRound,       color: "text-amber-600",   bg: "bg-amber-50",   value: loading ? "—" : fmtNum(block?.agents_created || 0) },
  ];

  return (
    <div className={`bg-white border border-gray-200 rounded-sm p-5 mb-4 ring-1 ${accentMap.ring}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${accentMap.iconBg}`}>
            <Icon size={17} strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
            <div className="font-display text-lg font-bold text-gray-900 mt-0.5">Bangladesh Time · Asia/Dhaka</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold border ${accentMap.chip}`}>
          BDT · BD Time
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <MiniStat key={c.label} icon={c.icon} label={c.label} value={c.value} color={c.color} bg={c.bg} />
        ))}
      </div>
    </div>
  );
}


function StuckBetsBanner({ stuck }) {
  if (stuck.loading || stuck.count === 0) {
    return null; // hide when nothing is stuck — keep the dashboard clean
  }
  const ageLabel =
    stuck.oldest_min >= 1440
      ? `${Math.floor(stuck.oldest_min / 1440)}d`
      : stuck.oldest_min >= 60
      ? `${Math.floor(stuck.oldest_min / 60)}h`
      : `${stuck.oldest_min}m`;
  return (
    <Link
      to="/root/admin/bets/stuck"
      className="block group mb-6"
      data-testid="dashboard-stuck-bets-banner"
    >
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-sm p-4 sm:p-5 hover:border-amber-400 transition-colors">
        <div className="absolute inset-y-0 left-0 w-1 bg-amber-500" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-sm bg-amber-500/15 text-amber-700 flex items-center justify-center shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                Action Required · Auto-refreshes every 60s
              </div>
              <div className="font-display text-lg sm:text-xl font-bold text-amber-900 mt-0.5">
                {fmtNum(stuck.count)} stuck bet{stuck.count === 1 ? "" : "s"}
                <span className="text-amber-700 font-medium"> · ৳{fmtBDT(stuck.total_stake).replace("৳","")} at risk</span>
              </div>
              <div className="text-xs text-amber-700/80 mt-0.5">
                Oldest pending: <strong>{ageLabel}</strong>. Resolve in one click — credit wins, refund cancelled rounds, or mark losses.
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-amber-800 font-semibold text-sm shrink-0 group-hover:translate-x-0.5 transition-transform">
            Resolve now <ChevronRight size={16} />
          </div>
        </div>
      </div>
    </Link>
  );
}
